import apiCall from '@/utils/apiCall';
import { keyBy } from 'lodash';

export default {
    /**
     * Load show players
     *
     * @param {object} store        Vuex store.
     * @param {object} store.state  Vuex state.
     * @param {Function} store.commit Method to commit a mutation.
     * @param {object} payload     Payload data.
     * @param {string} payload.type Player type.
     * @param {number} payload.page Page number (for track players).
     * @param {number} payload.limit Items per page (for track players).
     * @returns {Promise}               Promise.
     */
    async loadPlayers({ state, commit }, { type, showHash, force = false, page = 1, limit = 10 }) {
        const isArchive = 'archive' === type;

        // For archive players, use old behavior
        if (isArchive) {
            if (state[`${type}Players`]?.[showHash] && ! force) {
                return;
            }

            const { status, data } = await apiCall(
                this._vm,
                'get',
                `/api/shows/${showHash}/players`,
                {
                    params: {
                        type,
                        tracks: 0,
                    },
                }
            );

            if (200 !== status) {
                throw data.message;
            }

            commit('setPlayers', {
                type,
                data: {
                    [showHash]: keyBy(data, 'hash'),
                },
                replace: true,
            });

            return;
        }

        // For track players, use page-based caching
        // Check if this page is already loaded
        const pageObj = state[`${type}Players`]?.[showHash]?.find(p => p.page === page);

        if (pageObj && ! force) {
            // Page already loaded, just update pagination to reflect current page
            const currentPagination = state[`${type}PlayersPagination`]?.[showHash];
            if (currentPagination) {
                commit('setPlayersPagination', {
                    type,
                    showHash,
                    pagination: {
                        ...currentPagination,
                        currentPage: page,
                    },
                });
            }
            return;
        }

        // Set loading state immediately
        commit('setPlayersPageState', {
            type,
            showHash,
            page,
            loading: true,
        });

        // Update current page immediately for instant UI feedback
        const currentPagination = state[`${type}PlayersPagination`]?.[showHash];
        if (currentPagination) {
            commit('setPlayersPagination', {
                type,
                showHash,
                pagination: {
                    ...currentPagination,
                    currentPage: page,
                },
            });
        }

        try {
            const { status, data } = await apiCall(
                this._vm,
                'get',
                `/api/shows/${showHash}/episodes`,
                {
                    params: {
                        page,
                        limit,
                    },
                }
            );

            if (200 !== status) {
                throw data.message;
            }

            // Store this page's data
            commit('setPlayersPageState', {
                type,
                showHash,
                page,
                data: keyBy(data.data, 'trackNo'),
                loading: false,
            });

            // Update pagination metadata
            commit('setPlayersPagination', {
                type,
                showHash,
                pagination: {
                    currentPage: data.current_page || data.meta?.current_page || page,
                    lastPage: data.last_page || data.meta?.last_page || 1,
                    perPage: data.per_page || data.meta?.per_page || limit,
                    total: data.total || data.meta?.total || 0,
                },
            });
        } catch (error) {
            // Set error state
            commit('setPlayersPageState', {
                type,
                showHash,
                page,
                loading: false,
                error: error.message || 'Failed to load players',
            });
            throw error;
        }
    },

    /**
     * Add player
     *
     * @param   {object}    store                   Vuex store.
     * @param   {Function}  store.commit            Method to commit a mutation.
     * @param   {object}    payload                 Payload data.
     * @param   {number}    payload.playerNumber    Player number.
     * @param   {number}    payload.showId          Show ID.
     * @param   {string}    payload.hash            Show Hash.
     *
     */
    async addPlayer({ commit }, { playerNumber, showId, hash }) {
        const { status, data } = await apiCall(
            this._vm,
            'post',
            '/api/players/archive',
            { name: `New archive player ${playerNumber}`, showId }
        );

        if (200 !== status) {
            throw data.message;
        }

        commit('addPlayer', {
            showHash: hash,
            data: {
                [data.hash]: { ...data },
            },
        });

        commit('clearRecentPlayers', { type: 'archive' });
    },

    /**
     * Update player
     *
     * @param   {object}    store                   Vuex store.
     * @param   {Function}  store.commit            Method to commit a mutation.
     * @param   {object}    payload                 Payload data.
     * @param   {string}    payload.type            Player type.
     * @param   {string}    payload.hash            Player Hash.
     * @param   {string}    payload.showHash        ShowHash.
     * @param   {object}    payload.playerData      New Player data.
     */
    async updatePlayer({ commit }, { type, hash, showHash, playerData }) {
        const { status, data } = await apiCall(
            this._vm,
            'put',
            `/api/players/archive/${hash}`,
            playerData
        );

        if (200 !== status) {
            throw data.message;
        }

        commit('updatePlayer', {
            showHash,
            playerKey: hash,
            type,
            data,
        });

        commit('clearRecentPlayers', { type });
    },

    /**
     * Set Episode Data
     *
     * @param   {object}    store                   Vuex store.
     * @param   {Function}  store.commit            Method to commit a mutation.
     * @param   {object}    payload                 Payload data.
     * @param   {string}    payload.type            Player type.
     * @param   {string}    payload.showHash        ShowHash.
     * @param   {number}    payload.trackNo         Track Number for track player
     * @param   {boolean}   payload.featured        Is episode featured?
     */
    async setEpisodeFeaturedStatus(
        { commit },
        { type, showHash, trackNo, featured }
    ) {
        const { status, data } = await apiCall(
            this._vm,
            'put',
            `/api/shows/${showHash}/episodes/${trackNo}/featured`,
            { featured }
        );

        if (200 !== status) {
            throw data.message;
        }

        commit('updatePlayer', {
            showHash,
            playerKey: trackNo,
            type,
            data,
        });
    },

    /**
     * Remove player
     *
     * @param   {object}    store                   Vuex store.
     * @param   {Function}  store.commit            Method to commit a mutation.
     * @param   {object}    payload                 Payload data.
     * @param   {number}    payload.type            Player type.
     * @param   {number}    payload.showHash        Show hash.
     * @param   {string}    payload.playerHash            Player Hash.
     *
     */
    async deletePlayer({ commit }, { type, showHash, playerHash }) {
        const { status, data } = await apiCall(
            this._vm,
            'delete',
            `/api/players/${type}/${playerHash}`
        );

        if (200 !== status) {
            throw data.message;
        }

        commit('deletePlayer', { showHash, playerHash, type });
        commit('clearRecentPlayers', { type });
    },

    /**
     * Load track player config
     *
     * @param   {object}    store                   Vuex store.
     * @param   {object}    store.state  Vuex state.
     * @param   {Function}  store.commit            Method to commit a mutation.
     * @param   {object}    payload                 Payload data.
     * @param   {string}    payload.showHash        Show hash
     * @param   {number}    payload.trackNo         Track number
     *
     */
    async loadTrackPlayerConfig({ commit, state }, { showHash, trackNo }) {
        if (state.trackPlayersConfig?.[showHash]?.[trackNo]) {
            return;
        }

        const { status, data } = await apiCall(
            this._vm,
            'get',
            `/api/players/track/${showHash}/${trackNo}`
        );

        if (200 !== status) {
            throw data.message;
        }

        commit('setTrackPlayerConfig', { showHash, trackNo, data });
    },

    /**
     * Update track player config
     *
     * @param   {object}    store                   Vuex store.
     * @param   {Function}  store.commit            Method to commit a mutation.
     * @param   {object}    payload                 Payload data.
     * @param   {string}    payload.showHash        Show hash
     * @param   {number}    payload.trackNo         Track number
     * @param   {object}    payload.playerData      Episode config data
     *
     */
    async updateTrackPlayerConfig(
        { commit },
        { showHash, trackNo, playerData }
    ) {
        const { status, data } = await apiCall(
            this._vm,
            'put',
            `/api/players/track/${showHash}/${trackNo}`,
            { ...playerData }
        );

        if (200 !== status) {
            throw data.message;
        }

        commit('updateTrackPlayerConfig', { showHash, trackNo, data });
    },

    /**
     * Gets recent players
     *
     * @param   {object}    store                   Vuex store.
     * @param   {Function}  store.commit            Method to commit a mutation.
     * @param   {object}    payload                 Payload data.
     * @param   {string}    payload.url             Url
     * @param   {string}    payload.itemsType       Players type
     * @param   {number}    payload.itemsLimit      Number of players
     * @param   {string}    payload.hash            Show hash
     *
     */
    async getRecentPlayers({ commit }, { url, itemsType, itemsLimit, hash }) {
        const params = {
            type: itemsType,
            limit: itemsLimit,
        };

        if ('archive' === itemsType) {
            params.tracks = 0;
        }

        const { status, data } = await apiCall(this._vm, 'get', url, {
            params: { ...params },
        });

        if (200 !== status) {
            throw data.message;
        }

        commit('addRecentPlayers', {
            type: itemsType,
            showHash: hash,
            players: data,
        });
    },
};
